/* eslint promise/catch-or-return: ["warn", { allowFinally: true }]  */
import React from "react";
//Limitless imports
import { log as pilog } from "@limitless/common/utils";
import { INITIAL_FIELD, INITIAL_BOOL_FIELD, INITIAL_OBJECT_FIELD, getAllFieldValues } from "../../utils/FieldValidators";
import { useAuthenticationContext } from "../authentication/AuthenticationContext";
import { authenticateUser, doesUserHaveValidSession, triggerForgotPassword, confirmForgotPassword } from "../../apis/authentication-api/AuthenticationAPI";
import { useLoginWrapperContext } from "../..";
import { signUpUser, confirmSignUpRegistrationCode, resendSignUpRegistrationCode } from "../../apis/sign-up-api/SignUpAPI";
import { useExpertProfileContext } from "../expert-profile/ExpertProfileContext";
import { doesUserHavePolicy } from "../../../portal";
import { sendSMSVerificationCode } from "../../apis/authentication-api/AuthenticationAPI";
const getCognitoData = idToken => {
  const log = pilog.getLogger("SignInHook getCognitoData");
  log.debug("Token payload", idToken.payload);
  let policies = [];
  let phoneDetails;
  try {
    var _idToken$payload, _idToken$payload2;
    policies = JSON.parse(idToken.payload["userPolicies"]);
    log.debug("Policies assigned to user", policies);
    policies = policies ? policies : [];
    phoneDetails = {
      phoneNumber: (_idToken$payload = idToken.payload) === null || _idToken$payload === void 0 ? void 0 : _idToken$payload.phone_number,
      isPhoneVerified: ((_idToken$payload2 = idToken.payload) === null || _idToken$payload2 === void 0 ? void 0 : _idToken$payload2.phone_number_verified) === true
    };
  } catch (error) {
    log.info("Unable to assign policies", error.message);
  }
  return {
    policies,
    phoneDetails
  };
};
const INITIAL_SIGN_UP_STATE = {
  email: INITIAL_FIELD,
  country: INITIAL_OBJECT_FIELD,
  password: INITIAL_FIELD,
  confirmPassword: INITIAL_FIELD,
  hasAgreedToTerms: INITIAL_BOOL_FIELD,
  shareNameEmail: INITIAL_BOOL_FIELD,
  rcvThirdPartyEmails: INITIAL_BOOL_FIELD,
  registrationCode: INITIAL_FIELD
};
let cognitoData;
export const useSignIn = () => {
  const [loading, setLoading] = React.useState(false);
  const [email, setEmail] = React.useState(INITIAL_FIELD);
  const [password, setPassword] = React.useState(INITIAL_FIELD);
  const [passwordResetError, setPasswordResetError] = React.useState("");
  const [forgotPasswordError, setForgotPasswordError] = React.useState("");
  const [signUpState, setSignUpState] = React.useState(INITIAL_SIGN_UP_STATE);
  const {
    saveCognitoData,
    profile
  } = useExpertProfileContext();
  const {
    mfaPhoneChallengeCB,
    mfaVerifyScreenCallback,
    mfaVerifyScreenSuccessCB,
    mfaLoginState
  } = useLoginWrapperContext();
  const {
    setUserAsAuthenticated,
    updateAuthError,
    clearAuthError,
    resetAuth,
    signOut,
    appAccessPolicy
  } = useAuthenticationContext();
  const postAuthenticationActions = async idToken => {
    const {
      policies,
      phoneDetails
    } = getCognitoData(idToken);
    if (policies.length === 0 || !doesUserHavePolicy(appAccessPolicy, {
      cognitoPolicies: policies
    })) {
      signOut("You are not authorised to view this portal");
    } else {
      saveCognitoData({
        policies,
        _phoneDetails: phoneDetails
      });
      cognitoData = phoneDetails;
      clearAuthError();
      if (process.env.REACT_APP_NAME !== "ExpertMobileApp") {
        setUserAsAuthenticated();
      }
    }
  };
  const signIn = (onSignInSuccess, storageCallback) => {
    signinUser(email.value, password.value, onSignInSuccess, storageCallback, null);
  };
  const signinUser = async (email, password, onSignInSuccess, storageCallback, cognitoErrorCallback = null) => {
    setLoading(true);
    authenticateUser(email, password, storageCallback, mfaPhoneChallengeCB, mfaVerifyScreenCallback).then(async idToken => {
      var _cognitoData, _cognitoData2;
      postAuthenticationActions(idToken);
      if ((_cognitoData = cognitoData) !== null && _cognitoData !== void 0 && _cognitoData.phoneNumber && !((_cognitoData2 = cognitoData) !== null && _cognitoData2 !== void 0 && _cognitoData2.isPhoneVerified)) {
        await sendSMSVerificationCode();
      } else {
        mfaVerifyScreenSuccessCB(cognitoData);
      }

      // email & password will be stored on keychain for the biometric authentication - EMA
      onSignInSuccess({
        email,
        password
      });
    }).catch(authErrorMessage => {
      if (cognitoErrorCallback) {
        cognitoErrorCallback(authErrorMessage);
      }
      updateAuthError(authErrorMessage);
    }).finally(() => setLoading(false));
  };
  const checkUserAccessTokens = (callback, postTokenRefreshCallback) => {
    doesUserHaveValidSession(postTokenRefreshCallback).then(idToken => {
      postAuthenticationActions(idToken);
      if (process.env.REACT_APP_NAME !== "ExpertMobileApp") {
        callback();
      }
    }).catch(() => callback());
  };
  const forgotPassword = onVerificationCodeSent => {
    setLoading(true);
    triggerForgotPassword(email.value).then(() => {
      if (typeof onVerificationCodeSent === "function") {
        onVerificationCodeSent();
      }
      clearErrors();
    }).catch(() => setPasswordResetError("Failed to reset password at this time")).finally(() => setLoading(false));
  };
  const confirmPassword = (registrationCode, newPassword, successCallback) => {
    setLoading(true);
    confirmForgotPassword(email.value, registrationCode, newPassword).then(() => {
      resetAuth();
      successCallback();
    }).catch(() => setForgotPasswordError("Code or password must be invalid")).finally(() => setLoading(false));
  };
  const clearErrors = () => {
    setPasswordResetError("");
    setForgotPasswordError("");
    clearAuthError();
  };
  const signUp = onSignUpSuccess => {
    setLoading(true);
    const user = getAllFieldValues(signUpState);
    signUpUser(user).then(() => onSignUpSuccess()).catch(errorMessage => updateAuthError(errorMessage)).finally(() => setLoading(false));
  };
  const confirmRegistration = (onSignUpSuccess, storageCallback) => {
    const {
      email,
      registrationCode,
      password
    } = signUpState;
    setLoading(true);
    confirmSignUpRegistrationCode(email.value, registrationCode.value).then(() => {
      signinUser(email.value, password.value, onSignUpSuccess, storageCallback);
    }).catch(errorMessage => {
      updateAuthError(errorMessage);
      setLoading(false);
    });
  };
  const resendRegistrationCode = () => {
    setLoading(true);
    resendSignUpRegistrationCode(signUpState.email.value).then(() => clearAuthError()).catch(errorMessage => updateAuthError(errorMessage)).finally(() => setLoading(false));
  };
  return {
    email,
    setEmail,
    signIn,
    signinUser,
    loading,
    password,
    setPassword,
    checkUserAccessTokens,
    postAuthenticationActions,
    passwordResetError,
    clearErrors,
    forgotPassword,
    forgotPasswordError,
    setForgotPasswordError,
    confirmPassword,
    signUpState,
    setSignUpState,
    signUp,
    confirmRegistration,
    resendRegistrationCode
  };
};